import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  catchError,
  concatMap,
  defer,
  finalize,
  EMPTY,
  expand,
  reduce,
  from,
  map,
  Observable,
  of,
  shareReplay,
  throwError,
  tap,
} from 'rxjs';
import { ApiResponse } from '@core/interfaces/apiResponse.interfaces';
import { PaginationResult } from '@core/interfaces/pagination.interface';
import { appDb } from '@core/db/app-db';
import { BaseHttpService } from '@core/services/base-http.service';
import { ConnectivityService } from '@core/services/connectivity.service';
import { SyncService } from '@core/services/sync.service';
import { TenantSessionService } from '@core/services/tenant-session.service';
import { httpParamsToObject } from '@core/utils/util';
import { environment } from '../../../../environments/environment';
import {
  ICashMovement,
  ICashMovementFilterParams,
} from '@models/cash.interface';
import {
  CloseCashSessionPayload,
  ICashSession,
  ICashSessionFilterParams,
  ICashSessionPaymentMethodsSummary,
  OpenCashSessionPayload,
} from '@models/cashSession.interface';
import {
  createClientOperationId,
  createLocalId,
  shouldUseOfflineFallback,
  withSyncMetadata,
} from '@core/services/offline.utils';

@Injectable({ providedIn: 'root' })
export class CashSessionService extends BaseHttpService<ICashSession> {
  private readonly cashMovementBaseUrl = `${environment.api_rest_url}/cashMovement`;
  private readonly connectivityService = inject(ConnectivityService);
  private readonly syncService = inject(SyncService);
  private readonly tenantSessionService = inject(TenantSessionService);
  private currentCashSessionRequest$: Observable<ICashSession | null> | null = null;
  private currentPaymentMethodsRequest$: Observable<ICashSessionPaymentMethodsSummary | null> | null = null;

  constructor() {
    super(inject(HttpClient), 'cashSession');
  }

  getCashSessions(params: ICashSessionFilterParams) {
    if (!this.connectivityService.isOnline()) {
      return from(this.getLocalCashSessionsResponse(params));
    }

    return this.findWithFiltersPaginated(
      params,
      '',
    ).pipe(
      tap(async (response) => {
        const items = response.data?.data ?? [];
        await this.syncCashSessionsSnapshot(items, params,
          response.data?.page === 1 && response.data.total <= items.length &&
          !params.search && !params.cash_register_id && !params.opened_by_user_id && !params.start_date && !params.end_date);
      }),
      catchError(() => from(this.getLocalCashSessionsResponse(params))),
    );
  }

  getCashClosures(params: ICashSessionFilterParams) {
    return this.findWithFiltersPaginated(
      params,
      'closures',
    );
  }

  getAllOpenCashSessions(): Observable<ICashSession[]> {
    const filters: ICashSessionFilterParams = { page: 1, limit: 100, status: 'OPEN', sortBy: 'id', sort_order: 'ASC' };
    if (!this.connectivityService.isOnline()) {
      return from(this.getLocalCashSessionsResponse({ ...filters, limit: Number.MAX_SAFE_INTEGER }))
        .pipe(map(response => response.data?.data ?? []));
    }
    const readPage = (page: number) => this.findWithFiltersPaginated({ ...filters, page }, '');
    return readPage(1).pipe(
      expand(response => {
        const page = response.data;
        if (!page || page.page < 1 || page.limit < 1) throw new Error('Respuesta de sesiones sin paginación válida.');
        if (!page.data.length && page.page * page.limit < page.total) throw new Error('Listado de sesiones incompleto.');
        return page.data.length > 0 && page.page * page.limit < page.total ? readPage(page.page + 1) : EMPTY;
      }),
      reduce((items: ICashSession[], response) => [...items, ...(response.data?.data ?? [])], [] as ICashSession[]),
      // Reconcile only after obtaining the entire snapshot, never after an individual page.
      concatMap(items => from(this.syncCashSessionsSnapshot(items, filters, true)).pipe(
        map(() => items), catchError(() => of(items)),
      )),
    );
  }

  getCurrentCashSession(): Observable<ICashSession | null> {
    if (!this.connectivityService.isOnline()) {
      return from(appDb.cashSessions.toArray()).pipe(
        map((items) => this.findLocalCurrentOpenSession(items)),
      );
    }

    if (this.currentCashSessionRequest$) {
      return this.currentCashSessionRequest$;
    }

    this.currentCashSessionRequest$ = this.http
      .get<ApiResponse<ICashSession>>(`${this.baseUrl}/current`)
      .pipe(
        tap(async (response) => {
          if (response?.data) {
            await appDb.cashSessions.put(withSyncMetadata(response.data));
            return;
          }

          await this.clearLocalCurrentOpenSession();
        }),
        map((response) => {
          if (response?.code === 200 && response?.data == null) {
            return null;
          }

          return response?.data ?? null;
        }),
        catchError((error) => {
          if (this.isCashSessionNotOpenError(error)) {
            return of(null);
          }

          return from(appDb.cashSessions.toArray()).pipe(
            map((items) => this.findLocalCurrentOpenSession(items)),
          );
        }),
        finalize(() => {
          this.currentCashSessionRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.currentCashSessionRequest$;
  }

  getCurrentCashSessionPaymentMethods(): Observable<ICashSessionPaymentMethodsSummary | null> {
    if (!this.connectivityService.isOnline()) {
      return of(null);
    }

    if (this.currentPaymentMethodsRequest$) {
      return this.currentPaymentMethodsRequest$;
    }

    this.currentPaymentMethodsRequest$ = this.http
      .get<ApiResponse<ICashSessionPaymentMethodsSummary>>(
        `${this.baseUrl}/current/payment-methods`,
      )
      .pipe(
        map((response) => {
          if (response?.code === 200 && response?.data == null) {
            return null;
          }

          return response?.data ?? null;
        }),
        catchError((error) => {
          if (this.isCashSessionNotOpenError(error)) {
            return of(null);
          }

          return throwError(() => error);
        }),
        finalize(() => {
          this.currentPaymentMethodsRequest$ = null;
        }),
        shareReplay(1),
      );

    return this.currentPaymentMethodsRequest$;
  }

  getCashSessionPaymentMethodsById(
    sessionId: number,
  ): Observable<ICashSessionPaymentMethodsSummary | null> {
    if (!this.connectivityService.isOnline()) {
      return of(null);
    }

    return this.http
      .get<ApiResponse<ICashSessionPaymentMethodsSummary>>(
        `${this.baseUrl}/${sessionId}/payment-methods`,
      )
      .pipe(map((response) => response?.data ?? null));
  }

  getCashClosureById(id: number): Observable<ICashSession | null> {
    if (!this.connectivityService.isOnline()) {
      return from(appDb.cashSessions.get(id)).pipe(map((item) => item ?? null));
    }

    return this.http
      .get<ApiResponse<ICashSession>>(`${this.baseUrl}/closures/${id}`)
      .pipe(map((response) => response?.data ?? null));
  }

  getCashSessionById(id: number): Observable<ICashSession | null> {
    if (!this.connectivityService.isOnline()) {
      return from(appDb.cashSessions.get(id)).pipe(map((item) => item ?? null));
    }

    return this.http
      .get<ApiResponse<ICashSession>>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(async (response) => {
          if (response.data) {
            await appDb.cashSessions.put(withSyncMetadata(response.data));
          }
        }),
        map((response) => response?.data ?? null),
        catchError(() => from(appDb.cashSessions.get(id)).pipe(map((item) => item ?? null))),
      );
  }

  getCashSessionMovements(
    sessionId: number,
    params: ICashMovementFilterParams | HttpParams,
  ): Observable<ApiResponse<PaginationResult<ICashMovement>>> {
    const normalizedParams =
      params instanceof HttpParams ? httpParamsToObject(params) : (params ?? {});
    let httpParams = new HttpParams();

    Object.entries(normalizedParams).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }

      httpParams = httpParams.set(key, String(value));
    });

    if (!this.connectivityService.isOnline()) {
      return from(this.getLocalCashSessionMovementsResponse(sessionId, normalizedParams));
    }

    return this.http.get<ApiResponse<PaginationResult<ICashMovement>>>(
      `${this.cashMovementBaseUrl}/session/${sessionId}`,
      { params: httpParams },
    ).pipe(
      catchError((error) => {
        if (!shouldUseOfflineFallback(error)) {
          return throwError(() => error);
        }

        return from(this.getLocalCashSessionMovementsResponse(sessionId, normalizedParams));
      }),
    );
  }

  openCashSession(payload: OpenCashSessionPayload) {
    this.clearCurrentSessionCache();

    if (!this.connectivityService.isOnline()) {
      return defer(() => from(this.openOffline(payload)));
    }

    return this.http.post<ApiResponse<ICashSession>>(this.baseUrl, payload).pipe(
      tap(async (response) => {
        if (response.data) {
          await appDb.cashSessions.put(withSyncMetadata(response.data));
        }
      }),
      catchError((error) => {
        if (!shouldUseOfflineFallback(error)) {
          return throwError(() => error);
        }

        return defer(() => from(this.openOffline(payload)));
      }),
    );
  }

  closeCashSession(payload: CloseCashSessionPayload) {
    this.clearCurrentSessionCache();

    if (!this.connectivityService.isOnline()) {
      return defer(() => from(this.closeOffline(payload)));
    }

    return this.http.post<ApiResponse<ICashSession>>(
      `${this.baseUrl}/close`,
      payload,
    ).pipe(
      tap(async (response) => {
        if (response.data) {
          await appDb.cashSessions.put(withSyncMetadata(response.data));
        }
      }),
      catchError((error) => {
        if (!shouldUseOfflineFallback(error)) {
          return throwError(() => error);
        }

        return defer(() => from(this.closeOffline(payload)));
      }),
    );
  }

  private async openOffline(payload: OpenCashSessionPayload): Promise<ApiResponse<ICashSession>> {
    const localId = createLocalId();
    const clientOperationId = createClientOperationId();
    const user = this.tenantSessionService.getCurrentUser();
    const company = this.tenantSessionService.getCurrentCompany();
    const session = withSyncMetadata<ICashSession>({
      id: localId,
      business_id: Number(company?.business_id ?? 0),
      company_id: company?.id ?? null,
      cash_register_id: payload.cash_register_id ?? null,
      opened_by_user_id: Number(user?.id ?? 0),
      opening_amount: payload.opening_amount ?? 0,
      status: 'OPEN',
      notes: payload.notes ?? null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as ICashSession, {
      synchronized: false,
      clientOperationId,
    });

    await appDb.cashSessions.put(session);
    await this.syncService.enqueue('cashSession', 'create', {
      ...payload,
      id: localId,
      clientOperationId,
      updatedAt: session.updatedAt,
    });

    return {
      code: 202,
      status: 'success',
      message: 'Apertura de caja guardada localmente. Se sincronizará al recuperar conexión.',
      data: session,
    };
  }

  private async closeOffline(payload: CloseCashSessionPayload): Promise<ApiResponse<ICashSession>> {
    const current = await appDb.cashSessions.get(payload.cash_session_id);
    const session = withSyncMetadata<ICashSession>({
      ...(current ?? { id: payload.cash_session_id }),
      status: 'CLOSED',
      cash_left_amount: payload.cash_left_amount,
      closing_notes: payload.notes ?? null,
      closed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as ICashSession, {
      synchronized: false,
      clientOperationId: current?.clientOperationId ?? createClientOperationId(),
    });

    await appDb.cashSessions.put(session);
    await this.syncService.enqueue('cashSession', 'update', {
      ...payload,
      id: payload.cash_session_id,
      updatedAt: session.updatedAt,
    });

    return {
      code: 202,
      status: 'success',
      message: 'Cierre de caja guardado localmente. Se sincronizará al recuperar conexión.',
      data: session,
    };
  }

  private clearCurrentSessionCache(): void {
    this.currentCashSessionRequest$ = null;
    this.currentPaymentMethodsRequest$ = null;
  }

  private async getLocalCashSessionsResponse(
    params: ICashSessionFilterParams,
  ): Promise<ApiResponse<PaginationResult<ICashSession>>> {
    const items = await appDb.cashSessions.toArray();
    const filteredItems = items
      .filter((item) => {
        if (
          params.status &&
          String(item.status ?? '').trim() !== String(params.status).trim()
        ) {
          return false;
        }

        if (
          params.opened_by_user_id &&
          Number(item.opened_by_user_id ?? 0) !==
            Number(params.opened_by_user_id)
        ) {
          return false;
        }

        if (
          params.cash_register_id &&
          Number(item.cash_register_id ?? 0) !==
            Number(params.cash_register_id)
        ) {
          return false;
        }

        const createdAt = item.created_at ? new Date(item.created_at) : null;
        const startDate = params.start_date
          ? new Date(`${params.start_date}T00:00:00`)
          : null;
        const endDate = params.end_date
          ? new Date(`${params.end_date}T23:59:59.999`)
          : null;

        if (
          startDate &&
          createdAt &&
          createdAt.getTime() < startDate.getTime()
        ) {
          return false;
        }

        if (
          endDate &&
          createdAt &&
          createdAt.getTime() > endDate.getTime()
        ) {
          return false;
        }

        return true;
      })
      .sort((left, right) => {
        const sortField = String(params.sortBy ?? 'created_at');
        const sortDirection = params.sort_order === 'ASC' ? 1 : -1;
        const leftValue = left[sortField as keyof ICashSession];
        const rightValue = right[sortField as keyof ICashSession];

        const normalizeSortValue = (value: unknown): number | string => {
          if (sortField.endsWith('_at') || value instanceof Date) {
            return value
              ? new Date(value as string | number | Date).getTime()
              : 0;
          }

          const numericValue = Number(value ?? 0);

          if (!Number.isNaN(numericValue)) {
            return numericValue;
          }

          return String(value ?? '').trim().toLocaleLowerCase('es');
        };

        const normalizedLeft = normalizeSortValue(leftValue);
        const normalizedRight = normalizeSortValue(rightValue);

        if (normalizedLeft === normalizedRight) {
          return Number(right.id ?? 0) - Number(left.id ?? 0);
        }

        return normalizedLeft > normalizedRight
          ? sortDirection
          : -sortDirection;
      });

    const page = params.page ?? 1;
    const limit = params.limit ?? Math.max(filteredItems.length, 1);
    const start = (page - 1) * limit;
    const paginatedItems = filteredItems.slice(start, start + limit);

    return {
      code: 200,
      status: 'success',
      message: 'Cajas obtenidas desde almacenamiento local.',
      data: {
        total: filteredItems.length,
        page,
        limit,
        data: paginatedItems,
      },
    };
  }

  private async getLocalCashSessionMovementsResponse(
    sessionId: number,
    params: Record<string, any>,
  ): Promise<ApiResponse<PaginationResult<ICashMovement>>> {
    const items = await appDb.cashMovements
      .where('cash_session_id')
      .equals(sessionId)
      .toArray();
    const filteredItems = items.sort((left, right) => {
      const leftDate = new Date(left.created_at ?? left.updated_at ?? 0).getTime();
      const rightDate = new Date(right.created_at ?? right.updated_at ?? 0).getTime();
      return rightDate - leftDate;
    });
    const page = Number(params['page'] ?? 1) || 1;
    const limit = Number(params['limit'] ?? Math.max(filteredItems.length, 1)) || 1;
    const start = (page - 1) * limit;

    return {
      code: 200,
      status: 'success',
      message: 'Movimientos de caja obtenidos desde almacenamiento local.',
      data: {
        total: filteredItems.length,
        page,
        limit,
        data: filteredItems.slice(start, start + limit),
      },
    };
  }

  private async syncCashSessionsSnapshot(
    items: ICashSession[],
    params: ICashSessionFilterParams,
    complete = false,
  ): Promise<void> {
    if (items.length > 0) {
      await appDb.cashSessions.bulkPut(items.map((item) => withSyncMetadata(item)));
    }

    if (!complete || String(params.status ?? '').toUpperCase() !== 'OPEN') {
      return;
    }

    const company = this.tenantSessionService.getCurrentCompany();
    const businessId = Number(company?.business_id ?? 0);
    const companyId = String(company?.id ?? '').trim();
    const remoteIds = new Set(items.map((item) => Number(item.id)).filter((id) => id > 0));
    const localOpenSessions = await appDb.cashSessions.toArray();

    await Promise.all(
      localOpenSessions
        .filter((item) => String(item.status ?? '').toUpperCase() === 'OPEN')
        .filter((item) => Number(item.synchronized ?? true) !== 0)
        .filter((item) => {
          const itemBusinessId = Number(item.business_id ?? 0);
          const itemCompanyId = String(item.company_id ?? '').trim();

          if (businessId > 0 && itemBusinessId > 0 && itemBusinessId !== businessId) {
            return false;
          }

          if (companyId && itemCompanyId && itemCompanyId !== companyId) {
            return false;
          }

          return !remoteIds.has(Number(item.id));
        })
        .map((item) => appDb.cashSessions.delete(Number(item.id))),
    );
  }

  private async clearLocalCurrentOpenSession(): Promise<void> {
    const user = this.tenantSessionService.getCurrentUser();
    const company = this.tenantSessionService.getCurrentCompany();
    const userId = Number(user?.id ?? 0);
    const businessId = Number(company?.business_id ?? 0);
    const companyId = String(company?.id ?? '').trim();
    const localOpenSessions = await appDb.cashSessions.toArray();

    await Promise.all(
      localOpenSessions
        .filter((item) => String(item.status ?? '').toUpperCase() === 'OPEN')
        .filter((item) => Number(item.synchronized ?? true) !== 0)
        .filter((item) => !userId || Number(item.opened_by_user_id ?? 0) === userId)
        .filter((item) => !businessId || Number(item.business_id ?? 0) === businessId)
        .filter((item) => !companyId || String(item.company_id ?? '').trim() === companyId)
        .map((item) => appDb.cashSessions.delete(Number(item.id))),
    );
  }

  private findLocalCurrentOpenSession(items: ICashSession[]): ICashSession | null {
    const user = this.tenantSessionService.getCurrentUser();
    const company = this.tenantSessionService.getCurrentCompany();
    const userId = Number(user?.id ?? 0);
    const businessId = Number(company?.business_id ?? 0);
    const companyId = String(company?.id ?? '').trim();

    return items.find((item) => {
      if (String(item.status ?? '').toUpperCase() !== 'OPEN') {
        return false;
      }

      if (userId > 0 && Number(item.opened_by_user_id ?? 0) !== userId) {
        return false;
      }

      if (businessId > 0 && Number(item.business_id ?? 0) !== businessId) {
        return false;
      }

      if (companyId && String(item.company_id ?? '').trim() !== companyId) {
        return false;
      }

      return true;
    }) ?? null;
  }

  private isCashSessionNotOpenError(error: unknown): boolean {
    const typedError = error as {
      status?: unknown;
      code?: unknown;
      error?: {
        code?: unknown;
        error?: {
          code?: unknown;
        };
      };
    } | null;
    const statusCandidates = [
      typedError?.status,
      typedError?.code,
      typedError?.error?.code,
    ];
    const status = statusCandidates
      .map((candidate) => Number(candidate))
      .find((candidate) => Number.isFinite(candidate));
    const errorCode =
      typedError?.error?.error?.code ??
      typedError?.error?.code;

    return status === 404 && errorCode === 'CASH_SESSION_NOT_OPEN';
  }
}
